<script setup>
// Inspector do nó "Criar tarefa": para quem vai a tarefa, quais tipos a IA
// pode criar e o prazo máximo. O servidor lê esta config.
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const store = useStore();

const TYPES = ['call', 'proposal', 'task', 'follow_up'];
const ASSIGNEE_MODES = ['card_owner', 'conversation_assignee', 'fixed'];
const users = computed(() => store.getters['agents/getAgents'] || []);
const types = computed(() => props.data.allowedTypes || TYPES);
const mode = computed(() => props.data.assigneeMode || 'card_owner');
const maxDays = computed(() => props.data.maxDays || 7);

const typeLabel = type =>
  ({
    call: t('AI_AGENTS.FLOW.CRM.TYPE_call'),
    proposal: t('AI_AGENTS.FLOW.CRM.TYPE_proposal'),
    task: t('AI_AGENTS.FLOW.CRM.TYPE_task'),
    follow_up: t('AI_AGENTS.FLOW.CRM.TYPE_follow_up'),
  })[type];
const modeLabel = value =>
  ({
    card_owner: t('AI_AGENTS.FLOW.CRM.ASSIGNEE_CARD_OWNER'),
    conversation_assignee: t('AI_AGENTS.FLOW.CRM.ASSIGNEE_CONVERSATION'),
    fixed: t('AI_AGENTS.FLOW.CRM.ASSIGNEE_FIXED'),
  })[value];

const toggleType = type => {
  const next = types.value.includes(type)
    ? types.value.filter(x => x !== type)
    : [...types.value, type];
  emit('update', { allowedTypes: next.length ? next : TYPES });
};

const onMaxDays = e => {
  const value = Math.min(30, Math.max(1, Number(e.target.value) || 7));
  emit('update', { maxDays: value });
};

onMounted(() => {
  if (!users.value.length) store.dispatch('agents/get');
});
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-teal-3 text-n-teal-11"
        >
          <span class="i-lucide-list-checks text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.CRM.TITLE_CREATE_TASK') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <div class="mb-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
      <p class="text-sm text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.tool_create_task.DESC') }}
      </p>
    </div>

    <label
      class="mb-3 flex cursor-pointer items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
      </span>
      <input
        type="checkbox"
        class="size-4 shrink-0 accent-[#00897b]"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <label class="mb-1 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.CRM.ASSIGNEE') }}
    </label>
    <select
      :value="mode"
      class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      @change="emit('update', { assigneeMode: $event.target.value })"
    >
      <option v-for="m in ASSIGNEE_MODES" :key="m" :value="m">
        {{ modeLabel(m) }}
      </option>
    </select>

    <select
      v-if="mode === 'fixed'"
      :value="data.assigneeId || ''"
      class="mt-2 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      @change="
        emit('update', { assigneeId: Number($event.target.value) || null })
      "
    >
      <option value="">{{ t('AI_AGENTS.FLOW.CRM.ASSIGNEE_PICK') }}</option>
      <option v-for="u in users" :key="u.id" :value="u.id">
        {{ u.name }}
      </option>
    </select>

    <p class="mb-1 mt-3 text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.CRM.TYPES') }}
    </p>
    <label
      v-for="type in TYPES"
      :key="type"
      class="flex items-center gap-2 py-1 text-sm text-n-slate-12"
    >
      <input
        type="checkbox"
        class="accent-[#00897b]"
        :checked="types.includes(type)"
        @change="toggleType(type)"
      />
      {{ typeLabel(type) }}
    </label>

    <label class="mb-1 mt-3 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.CRM.MAX_DAYS') }}
    </label>
    <input
      type="number"
      min="1"
      max="30"
      :value="maxDays"
      class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      @change="onMaxDays"
    />

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
